import React, { useState } from 'react';
import { Bot, Sparkles, ExternalLink, MessageSquare, Send, Loader, AlertTriangle } from 'lucide-react';
import { askAiDirectly } from '../../../utils/ai';

const GeoTab = ({ analysis, aiConfig, availableModels, clientDomain }) => {
    // Local state for the simulator
    const [simProvider, setSimProvider] = useState(aiConfig.provider);
    const [simModel, setSimModel] = useState(aiConfig.model);
    const [simPrompt, setSimPrompt] = useState(`Cosa sai dirmi su "${analysis.keyword}"? \nParla dei migliori brand e delle opzioni disponibili.`);
    const [simResult, setSimResult] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Smart Prompts based on keyword
    const smartPrompts = [
        { label: 'Analisi Migliori', text: `Quali sono i migliori ${analysis.keyword} attualmente sul mercato? Fammi una lista commentata.` },
        { label: 'Intento Commerciale', text: `Voglio acquistare ${analysis.keyword}. Quali marche mi consigli e che budget devo prevedere?` },
        { label: 'Confronto', text: `Quali sono le differenze principali tra i vari tipi di ${analysis.keyword}?` },
        { label: 'Informazionale', text: `Spiegami in dettaglio come funziona ${analysis.keyword} e a cosa serve esattamente.` },
        { label: 'Reputazione Brand', text: `Quali sono le aziende più affidabili nel settore "${analysis.keyword}"?` }
    ];

    const handleSimulation = async () => {
        if (!aiConfig.apiKeys[simProvider]) {
            setError(`Chiave API per ${simProvider} mancante. Configurala nel pannello PRO.`);
            return;
        }

        setLoading(true);
        setError('');
        setSimResult('');

        try {
            const result = await askAiDirectly(simProvider, simModel, aiConfig.apiKeys[simProvider], simPrompt);
            setSimResult(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to format citations
    const getHostname = (url) => {
        try {
            return new URL(url).hostname.replace('www.', '');
        } catch (e) {
            return url;
        }
    };

    // Helper to highlight client domain in result
    const renderSimResult = () => {
        if (!simResult) {
            return (
                <div className="h-full flex flex-col items-center justify-center text-gray-600 opacity-50">
                    <span>Il risultato della simulazione apparirà qui...</span>
                </div>
            );
        }

        if (!clientDomain) {
            return <div className="whitespace-pre-wrap">{simResult}</div>;
        }

        // Simple highlighting regex
        const parts = simResult.split(new RegExp(`(${clientDomain})`, 'gi'));
        return (
            <div className="whitespace-pre-wrap">
                {parts.map((part, i) => (
                    part.toLowerCase() === clientDomain.toLowerCase() ? (
                        <span key={i} className="bg-yellow-500 text-black font-bold px-1 rounded">{part}</span>
                    ) : (
                        part
                    )
                ))}
            </div>
        );
    };

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-indigo-100 text-indigo-600 rounded-lg">
                    <Sparkles size={24} />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Analisi AI & GEO</h2>
                    <p className="text-gray-500">Monitora la presenza del tuo brand nelle risposte generative (SGE e Chat AI).</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT COLUMN: Google SGE Snapshot */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2 font-bold text-gray-700">
                            <Bot size={18} />
                            Google AI Overview (SGE)
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-bold ${analysis.aiOverview?.present ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                            {analysis.aiOverview?.present ? 'RILEVATO' : 'NON PRESENTE'}
                        </span>
                    </div>

                    <div className="p-6">
                        {analysis.aiOverview?.present ? (
                            <>
                                <div className="prose prose-sm max-w-none text-gray-600 mb-6 bg-indigo-50 p-4 rounded-lg border border-indigo-100 relative">
                                    <Sparkles size={16} className="absolute top-2 right-2 text-indigo-300" />
                                    {analysis.aiOverview.text}
                                </div>

                                <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                                    <ExternalLink size={12} />
                                    Fonti Citate (Citations)
                                </h4>
                                {analysis.aiOverview.references && analysis.aiOverview.references.length > 0 ? (
                                    <ul className="space-y-2">
                                        {analysis.aiOverview.references.map((ref, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm p-2 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-100">
                                                <div className="min-w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                                                    {idx + 1}
                                                </div>
                                                <div className="overflow-hidden">
                                                    <a href={ref.url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline truncate block">
                                                        {ref.title || getHostname(ref.url)}
                                                    </a>
                                                    <span className="text-xs text-gray-400 truncate block">{getHostname(ref.url)}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Nessuna fonte diretta citata nello snippet.</p>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12 text-gray-400">
                                <Bot size={48} className="mx-auto mb-3 opacity-20" />
                                <p>Nessun "AI Overview" rilevato per questa keyword.</p>
                                <p className="text-xs mt-2">Google non ha attivato la risposta generativa in questa SERP.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: LLM Visibility Simulator */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
                    <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2 font-bold text-gray-700">
                            <MessageSquare size={18} />
                            Simulatore Chat AI (LLM Visibility)
                        </div>
                        {clientDomain && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold border border-purple-200">
                                Tracking: {clientDomain}
                            </span>
                        )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                        <p className="text-sm text-gray-500 mb-4">
                            Simula come i principali motori AI rispondono a domande sul tuo brand o sui prodotti in questa nicchia.
                        </p>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Provider</label>
                                <select
                                    value={simProvider}
                                    onChange={(e) => {
                                        setSimProvider(e.target.value);
                                        // Reset model to first available or standard default if switching
                                        setSimModel('');
                                    }}
                                    className="w-full p-2 bg-gray-50 border border-gray-200 rounded text-sm outline-none focus:border-indigo-500"
                                >
                                    <option value="anthropic">Anthropic (Claude)</option>
                                    <option value="openai">OpenAI (ChatGPT)</option>
                                    <option value="google">Google (Gemini)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Modello</label>
                                <select
                                    value={simModel || (availableModels?.[simProvider]?.[0])}
                                    onChange={(e) => setSimModel(e.target.value)}
                                    className="w-full p-2 bg-gray-50 border border-gray-200 rounded text-sm outline-none focus:border-indigo-500"
                                >
                                    {!simModel && <option value="">Default (Auto)</option>}
                                    {availableModels?.[simProvider]?.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                    {/* Fallbacks if no models loaded */}
                                    {(!availableModels?.[simProvider] || availableModels[simProvider].length === 0) && (
                                        <>
                                            {simProvider === 'anthropic' && <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>}
                                            {simProvider === 'openai' && <option value="gpt-4o">GPT-4o</option>}
                                            {simProvider === 'google' && <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>}
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>

                        {/* Smart Prompts Chips */}
                        <div className="mb-3 flex flex-wrap gap-2">
                            {smartPrompts.map((p, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSimPrompt(p.text)}
                                    className="text-xs bg-gray-100 hover:bg-indigo-100 text-gray-600 hover:text-indigo-700 px-3 py-1.5 rounded-full transition-colors border border-gray-200"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <div className="mb-4 relative">
                            <textarea
                                value={simPrompt}
                                onChange={(e) => setSimPrompt(e.target.value)}
                                className="w-full p-3 bg-white border border-gray-300 rounded-lg text-sm focus:border-indigo-500 outline-none h-24 resize-none"
                                placeholder="Scrivi una domanda per l'AI..."
                            />
                            <button
                                onClick={handleSimulation}
                                disabled={loading}
                                className="absolute bottom-3 right-3 p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                                {loading ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
                                <AlertTriangle size={16} />
                                {error}
                            </div>
                        )}

                        <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto max-h-[300px] shadow-inner text-gray-300 font-mono text-sm leading-relaxed border border-gray-800">
                            {renderSimResult()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeoTab;
